import { Injectable, computed, inject, signal } from '@angular/core';

import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { OrdersServiceWithApplyToAll } from '@vectronic/formly-components';
import { AppError, UnknownError } from '@vectronic/oof/shared/util';
import { oofDtos as oofAdminDtos } from '@vectronic/openApi-admin';
import { oofDtos } from '@vectronic/openApi-customer';
import { LoggedInUserService } from '@vectronic/shared/data-access/logged-in-user';
import {
  CreateOrderItemConfiguration,
  DeleteOrderItemConfiguration,
  EditOrderItemConfigurationAttributes,
  EditOrderItemConfigurationFormData,
  OrdersFilter,
  OrdersRestService,
  SavedDeviceAllocations,
  UpdateOrder,
  UpdateOrderItem,
  withDevicesDistributedTo,
  withDevicesReturnedToPool,
} from '@vectronic/shared/oof/data-access';
import { Pageable } from '@vectronic/util';
import { isEqual, set } from 'es-toolkit/compat';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  groupBy,
  map,
  merge,
  mergeMap,
  share,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AddressesService } from './addresses.service';

interface OrdersState {
  orders: oofDtos.Order[];
  numberOfLoadedOrders: number;
  loading: boolean;
  initiallyLoaded: boolean;
  error: AppError | null;
  totalElements: number;
  pagination: {
    pageNumber: number;
    pageSize: number;
  };
  orderHistory: oofAdminDtos.OrderHistoryItem[];
  orderHistoryLoading: boolean;
}

interface LoadedOrdersPage {
  content?: oofDtos.Order[];
  totalElements?: number;
  pageable?: {
    pageNumber?: number;
    pageSize?: number;
  };
}

const DEFAULT_PAGINATION: Pageable = { pageNumber: 0, pageSize: 100 };

@Injectable({ providedIn: 'root' })
export class OrdersService extends OrdersServiceWithApplyToAll {
  private userService = inject(LoggedInUserService);
  private addressesService = inject(AddressesService);
  private ordersRestService = inject(OrdersRestService);

  // state
  private state = signal<OrdersState>({
    orders: [],
    numberOfLoadedOrders: 0,
    loading: false,
    initiallyLoaded: false,
    error: null,
    totalElements: 0,
    pagination: DEFAULT_PAGINATION,
    orderHistory: [],
    orderHistoryLoading: false,
  });

  // selectors
  orders = computed(() => this.state().orders);
  numberOfLoadedOrders = computed(() => this.state().numberOfLoadedOrders);
  loading = computed(() => this.state().loading);
  error = computed(() => this.state().error);
  initiallyLoaded = computed(() => this.state().initiallyLoaded);
  totalElements = computed(() => this.state().totalElements);
  pagination = computed(() => this.state().pagination);
  orderHistory = computed(() => this.state().orderHistory);
  orderHistoryLoading = computed(() => this.state().orderHistoryLoading);

  // sources
  readonly salesAndStatusFilter$ = new Subject<{
    sales: string;
    status: oofDtos.Order.StatusEnum;
  }>();
  readonly ordersFilter$ = new Subject<OrdersFilter>();
  // TODO:  have the same filter source for both roles (search term for user and admin + separate filters for admin)
  readonly adminOrdersFilter$ = new Subject<oofAdminDtos.OrderFilterRequest>();
  readonly changePage$ = new Subject<Pageable>();

  readonly updateOrder$ = new Subject<UpdateOrder>();
  readonly addOrderComment$ = new Subject<{ orderId: string; comment: string }>();
  readonly editOrderComment$ = new Subject<{
    orderId: string;
    commentId: string;
    comment: string;
  }>();
  readonly deleteOrderComment$ = new Subject<{ orderId: string; commentId: string }>();
  readonly reloadOrder$ = new Subject<string>();
  readonly deviceAllocationsSaved$ = new Subject<SavedDeviceAllocations>();
  readonly loadOrderHistory$ = new Subject<string>();
  readonly selectedOrderId$ = new Subject<string>();
  readonly updateOrderItem$ = new Subject<UpdateOrderItem>();
  readonly createOrderItemConfiguration$ = new Subject<CreateOrderItemConfiguration>();
  readonly updateOrderItemConfigurationAttributes$ =
    new Subject<EditOrderItemConfigurationAttributes>();
  readonly updateOrderItemConfigurationformData$ =
    new Subject<EditOrderItemConfigurationFormData>();
  readonly deleteOrderItemConfiguration$ = new Subject<DeleteOrderItemConfiguration>();
  readonly #orderReloadFinished$ = new Subject<oofDtos.Order>();
  readonly orderReloadFinished$ = this.#orderReloadFinished$.asObservable();

  private error$ = new Subject<AppError>();
  private readonly defaultAdminFilter: oofAdminDtos.OrderFilterRequest = {
    statuses: [oofDtos.Order.StatusEnum.AllUnresolved],
    salesEmails: this.userService.username() ? [this.userService.username()] : undefined,
  };

  private _salesAndStatusFilter$ = this.salesAndStatusFilter$.pipe(
    startWith({
      sales: this.userService.username(),
      status: oofDtos.Order.StatusEnum.AllUnresolved,
    }),
  );
  private _resetPage$: Observable<Pageable> = merge(
    this.salesAndStatusFilter$,
    this.ordersFilter$,
    this.adminOrdersFilter$,
  ).pipe(
    withLatestFrom(this.changePage$),
    map(([, page]) => ({ ...page, pageNumber: 0 })),
  );
  private _currentPage$: Observable<Pageable> = merge(this.changePage$, this._resetPage$).pipe(
    startWith(DEFAULT_PAGINATION),
    distinctUntilChanged((previous, current) => isEqual(previous, current)),
    tap((page) => this.state.update((state) => ({ ...state, loading: true, pagination: page }))),
  );
  private _ordersFilter$ = this.ordersFilter$.pipe(
    debounceTime(500),
    distinctUntilChanged((prev, cur) => isEqual(prev, cur)),
    filter((filter) => filter != null),
    startWith({} as OrdersFilter),
  );
  private _adminOrdersFilter$ = this.adminOrdersFilter$.pipe(
    map((filter) => this.normalizeAdminOrderFilter(filter)),
    startWith(this.defaultAdminFilter),
    distinctUntilChanged((previous, current) => isEqual(previous, current)),
  );

  private ordersLoaded$: Observable<LoadedOrdersPage> = this.userService.isAdmin()
    ? combineLatest([this._currentPage$, this._adminOrdersFilter$]).pipe(
        map(([page, adminFilter]) => ({ page, adminFilter })),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        tap(({ page }) =>
          this.state.update((state) => ({ ...state, loading: true, pagination: page })),
        ),
        switchMap(({ adminFilter, page }) =>
          this.ordersRestService
            .getOrdersByMultiSelectFilter(adminFilter, page.pageNumber, page.pageSize)
            .pipe(
              catchError((err) => {
                this.error$.next(err);
                this.state.update((state) => ({ ...state, loading: false }));
                return EMPTY;
              }),
            ),
        ),
      )
    : this._currentPage$.pipe(
        switchMap((page) =>
          this.ordersRestService.getOrdersByFilter({}, page.pageNumber, page.pageSize).pipe(
            catchError((err) => {
              this.error$.next(err);
              this.state.update((state) => ({ ...state, loading: false }));
              return EMPTY;
            }),
          ),
        ),
      );
  private orderUpdatedInternal$ = this.updateOrder$.pipe(
    switchMap((updateOrder) => {
      const currentOrder = this.orders().find((o) => o.id === updateOrder.orderId);
      return this.ordersRestService
        .updateOrder(
          updateOrder.orderId,
          {
            ...currentOrder,
            ...updateOrder.data,
          },
          updateOrder.sendNotification,
        )
        .pipe(
          // The customer BFF update endpoint omits adminComments from its response.
          // Preserve them from the pre-update state so the order history keeps showing comments.
          map((updatedOrder) => ({
            ...updatedOrder,
            adminComments: currentOrder?.adminComments,
          })),
          catchError((err) => {
            this.error$.next(err);
            this.state.update((state) => ({ ...state, loading: false }));
            return EMPTY;
          }),
        );
    }),
    share(),
  );
  readonly orderUpdated$ = this.orderUpdatedInternal$;
  private orderCommentsUpdated$ = merge(
    this.addOrderComment$.pipe(
      switchMap(({ orderId, comment }) =>
        this.ordersRestService.addOrderComment(orderId, comment).pipe(
          catchError(() => {
            this.error$.next(new UnknownError('Something went wrong while adding order comment'));
            return EMPTY;
          }),
        ),
      ),
    ),
    this.deleteOrderComment$.pipe(
      switchMap(({ orderId, commentId }) =>
        this.ordersRestService.deleteOrderComment(orderId, commentId).pipe(
          catchError(() => {
            this.error$.next(new UnknownError('Something went wrong while deleting order comment'));
            return EMPTY;
          }),
        ),
      ),
    ),
    this.editOrderComment$.pipe(
      switchMap(({ orderId, commentId, comment }) =>
        this.ordersRestService.editOrderComment(orderId, commentId, comment).pipe(
          catchError(() => {
            this.error$.next(new UnknownError('Something went wrong while editing order comment'));
            return EMPTY;
          }),
        ),
      ),
    ),
  );
  #orderReloaded$ = this.reloadOrder$.pipe(
    switchMap((orderId) =>
      this.ordersRestService.getOrderById(orderId).pipe(
        tap((order) => this.#orderReloadFinished$.next(order)),
        catchError((err) => {
          this.error$.next(err);
          return EMPTY;
        }),
      ),
    ),
  );
  #orderHistoryLoaded$ = this.loadOrderHistory$.pipe(
    tap(() =>
      this.state.update((state) => ({ ...state, orderHistoryLoading: true, orderHistory: [] })),
    ),
    switchMap((orderId) =>
      this.ordersRestService.getOrderHistory(orderId).pipe(
        catchError(() => {
          this.error$.next(new UnknownError('Something went wrong while loading order history'));
          this.state.update((state) => ({
            ...state,
            orderHistoryLoading: false,
            orderHistory: [],
          }));
          return EMPTY;
        }),
      ),
    ),
  );
  private addressUpdated$ = this.updateOrder$.pipe(
    filter(({ data }) => Boolean(data.deliveryAddress || data.invoiceAddress)),
    switchMap(() => this.orderUpdated$.pipe(first())),
  );
  private _selectedOrderId$ = combineLatest([
    this.selectedOrderId$,
    toObservable(this.initiallyLoaded).pipe(filter(Boolean)),
  ]).pipe(
    filter(([id]) => !this.orders().find((o) => o.id === id)),
    switchMap(([id]) =>
      this.ordersRestService.getOrderById(id).pipe(
        catchError((err) => {
          this.error$.next(err);
          return EMPTY;
        }),
      ),
    ),
  );
  private orderItemUpdated$ = this.updateOrderItem$.pipe(
    switchMap((updateOrderItem) =>
      this.ordersRestService
        .updateOrderItem(updateOrderItem.orderId, updateOrderItem.orderItemId, {
          ...this.orders()
            .find((o) => o.id === updateOrderItem.orderId)
            ?.orderItems.find((i) => i.id === updateOrderItem.orderItemId),
          ...updateOrderItem.data,
        })
        .pipe(
          map((orderItem) => ({ orderId: updateOrderItem.orderId, orderItem })),
          catchError((err) => {
            this.error$.next(err);
            return EMPTY;
          }),
        ),
    ),
  );
  /** Keeps the devices left to distribute true locally, without re-fetching the order. */
  private withRedistributedDevices(
    orderItem: oofDtos.OrderItem,
    redistribute: (allocations: oofDtos.DeviceAllocation[]) => oofDtos.DeviceAllocation[],
  ): oofDtos.OrderItem {
    const allocations = orderItem.deviceAllocations?.allocations;
    if (!allocations) return orderItem;
    return {
      ...orderItem,
      deviceAllocations: {
        ...orderItem.deviceAllocations,
        allocations: redistribute(allocations),
      },
    };
  }

  private orderItemConfigurationCreated$ = this.createOrderItemConfiguration$.pipe(
    switchMap((createOrderItemConfig) =>
      this.ordersRestService
        .createOrderItemConfiguration(
          createOrderItemConfig.orderId,
          createOrderItemConfig.orderItemId,
          createOrderItemConfig.data,
        )
        .pipe(
          map((result) => ({
            orderId: createOrderItemConfig.orderId,
            orderItemId: createOrderItemConfig.orderItemId,
            orderItemConfiguration: result.modifiedOrderItemConfig,
            // The response does not echo the devices back; the save is all-or-nothing, so
            // what we sent is what the configuration now covers.
            savedDevices: createOrderItemConfig.data.devices,
            newOrderItemStatus: result.orderItemStatus,
          })),
          catchError((err) => {
            this.error$.next(err);
            return EMPTY;
          }),
        ),
    ),
  );
  private orderItemConfigurationAttributesUpdated$ =
    this.updateOrderItemConfigurationAttributes$.pipe(
      switchMap((updateConfig) =>
        this.ordersRestService
          .updateOrderItemConfiguration(
            updateConfig.orderId,
            updateConfig.orderItemId,
            updateConfig.orderItemConfigurationId,
            {
              ...this.orders()
                .find((o) => o.id === updateConfig.orderId)
                .orderItems.find((i) => i.id === updateConfig.orderItemId)
                .orderItemConfigurations.find(
                  (c) => c.id === updateConfig.orderItemConfigurationId,
                ),
              ...updateConfig.data,
            },
            updateConfig.sendNotification,
          )
          .pipe(
            map((result) => ({
              orderId: updateConfig.orderId,
              orderItemId: updateConfig.orderItemId,
              orderItemConfigurationId: updateConfig.orderItemConfigurationId,
              orderItemConfiguration: result.modifiedOrderItemConfig,
              savedDevices: updateConfig.data.devices,
              newOrderItemStatus: result.orderItemStatus,
            })),
            catchError((err) => {
              this.error$.next(err);
              return EMPTY;
            }),
          ),
      ),
    );
  private orderItemConfigurationFormDataUpdated$ = this.updateOrderItemConfigurationformData$.pipe(
    groupBy((updateConfig) => updateConfig.orderItemConfigurationId),
    mergeMap((updateConfigs) =>
      updateConfigs.pipe(
        debounceTime(2000),
        concatMap((updateConfig) =>
          this.ordersRestService
            .updateOrderItemConfigurationFormData(
              this.getRecentConfigVersion(
                updateConfig.orderId,
                updateConfig.orderItemId,
                updateConfig.orderItemConfigurationId,
              ),
              updateConfig.orderId,
              updateConfig.orderItemId,
              updateConfig.orderItemConfigurationId,
              updateConfig.data.formData,
            )
            .pipe(
              map((result) => ({
                orderId: updateConfig.orderId,
                orderItemId: updateConfig.orderItemId,
                orderItemConfigurationId: updateConfig.orderItemConfigurationId,
                orderItemConfiguration: {
                  ...this.orders()
                    .find((o) => o.id === updateConfig.orderId)
                    .orderItems.find((i) => i.id === updateConfig.orderItemId)
                    .orderItemConfigurations.find(
                      (c) => c.id === updateConfig.orderItemConfigurationId,
                    ),
                  formData: result.modifiedFormData,
                  version: result.version,
                  status: result.configStatus,
                },
                // A form-data save never redistributes devices.
                savedDevices: undefined,
                newOrderItemStatus: result.itemStatus,
              })),
              catchError((err) => {
                this.error$.next(err);
                return EMPTY;
              }),
            ),
        ),
      ),
    ),
  );
  private orderItemConfigurationDeleted$ = this.deleteOrderItemConfiguration$.pipe(
    switchMap((deleteConfig) =>
      this.ordersRestService
        .deleteOrderItemConfiguration(
          deleteConfig.orderId,
          deleteConfig.orderItemId,
          deleteConfig.orderItemConfigurationId,
        )
        .pipe(
          map((result) => ({ ...deleteConfig, newOrderItemStatus: result.orderItemStatus })),
          catchError((err) => {
            this.error$.next(err);
            return EMPTY;
          }),
        ),
    ),
  );

  lastUpdatedOrderId$ = new BehaviorSubject<string>(undefined);

  constructor() {
    super();
    // reducers
    this.error$
      .pipe(takeUntilDestroyed())
      .subscribe((error) => this.state.update((state) => ({ ...state, error })));
    this._salesAndStatusFilter$
      .pipe(takeUntilDestroyed())
      .subscribe(() =>
        this.state.update((state) => ({ ...state, error: null, loading: true, orders: [] })),
      );

    this._ordersFilter$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.state.update((state) => ({ ...state, loading: true, orders: [] })));
    this._adminOrdersFilter$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.state.update((state) => ({ ...state, loading: true, orders: [] })));

    this.ordersLoaded$.pipe(takeUntilDestroyed()).subscribe((page) => {
      this.state.update((state) => ({
        ...state,
        loading: false,
        initiallyLoaded: true,
        error: null,
        orders: page.content ?? [],
        numberOfLoadedOrders: page.content?.length,
        totalElements: page.totalElements,
        pagination: {
          pageNumber: page.pageable?.pageNumber,
          pageSize: page.pageable?.pageSize,
        },
      }));
    });

    this.orderUpdated$.pipe(takeUntilDestroyed()).subscribe((order) => {
      this.state.update((state) => ({
        ...state,
        orders: state.orders.map((o) => (o.id === order.id ? order : o)),
        loading: false,
      }));
      this.loadOrderHistory$.next(order.id);
    });

    this.orderCommentsUpdated$.pipe(takeUntilDestroyed()).subscribe((order) => {
      this.state.update((state) => ({
        ...state,
        orders: state.orders.map((currentOrder) =>
          currentOrder.id === order.id ? order : currentOrder,
        ),
      }));
      this.loadOrderHistory$.next(order.id);
    });

    this.#orderReloaded$.pipe(takeUntilDestroyed()).subscribe((order) =>
      this.state.update((state) => ({
        ...state,
        orders: state.orders.map((o) => (o.id === order.id ? order : o)),
        error: null,
      })),
    );

    this.#orderHistoryLoaded$.pipe(takeUntilDestroyed()).subscribe((historyItems) =>
      this.state.update((state) => ({
        ...state,
        orderHistory: historyItems,
        orderHistoryLoading: false,
      })),
    );

    this._selectedOrderId$.pipe(takeUntilDestroyed()).subscribe((order) =>
      this.state.update((state) => ({
        ...state,
        orders: [...state.orders, order],
      })),
    );

    this.orderItemUpdated$.pipe(takeUntilDestroyed()).subscribe((result) =>
      this.state.update((state) => ({
        ...state,
        orders: state.orders.map((o) =>
          o.id === result.orderId
            ? {
                ...o,
                orderItems: o.orderItems.map((i) =>
                  i.id === result.orderItem.id ? result.orderItem : i,
                ),
              }
            : o,
        ),
      })),
    );

    this.orderItemConfigurationCreated$.pipe(takeUntilDestroyed()).subscribe((result) => {
      const redistributeSavedDevices = (allocations: oofDtos.DeviceAllocation[]) =>
        withDevicesDistributedTo(
          allocations,
          result.orderItemConfiguration.id,
          result.savedDevices,
        );
      this.state.update((state) => ({
        ...state,
        orders: state.orders.map((o) =>
          o.id === result.orderId
            ? {
                ...o,
                orderItems: o.orderItems.map((i) =>
                  i.id === result.orderItemId
                    ? this.withRedistributedDevices(
                        {
                          ...i,
                          orderItemConfigurations: i.orderItemConfigurations
                            ? [...i.orderItemConfigurations, result.orderItemConfiguration]
                            : [result.orderItemConfiguration],
                          status: result.newOrderItemStatus,
                        },
                        redistributeSavedDevices,
                      )
                    : i,
                ),
              }
            : o,
        ),
      }));
    });

    this.updateOrderItemConfigurationformData$
      .pipe(takeUntilDestroyed())
      .subscribe((updateOrderItemConfig) => {
        const withUpdatedFormData = (i: oofDtos.OrderItem) => ({
          ...i,
          orderItemConfigurations: i.orderItemConfigurations.map((c) =>
            c.id === updateOrderItemConfig.orderItemConfigurationId
              ? { ...c, formData: updateOrderItemConfig.data.formData }
              : c,
          ),
        });
        this.state.update((state) => ({
          ...state,
          orders: state.orders.map((o) =>
            o.id === updateOrderItemConfig.orderId
              ? {
                  ...o,
                  orderItems: o.orderItems.map((i) =>
                    i.id === updateOrderItemConfig.orderItemId ? withUpdatedFormData(i) : i,
                  ),
                }
              : o,
          ),
        }));
      });

    merge(
      this.orderItemConfigurationAttributesUpdated$,
      this.orderItemConfigurationFormDataUpdated$,
    )
      .pipe(takeUntilDestroyed())
      .subscribe((result) => {
        const withUpdatedConfiguration = (i: oofDtos.OrderItem) => ({
          ...i,
          orderItemConfigurations: i.orderItemConfigurations.map((c) =>
            c.id === result.orderItemConfigurationId ? result.orderItemConfiguration : c,
          ),
          status: result.newOrderItemStatus,
        });
        const redistributeSavedDevices = (allocations: oofDtos.DeviceAllocation[]) =>
          withDevicesDistributedTo(
            allocations,
            result.orderItemConfigurationId,
            result.savedDevices,
          );
        this.state.update((state) => ({
          ...state,
          orders: state.orders.map((o) =>
            o.id === result.orderId
              ? {
                  ...o,
                  orderItems: o.orderItems.map((i) =>
                    i.id === result.orderItemId
                      ? this.withRedistributedDevices(
                          withUpdatedConfiguration(i),
                          redistributeSavedDevices,
                        )
                      : i,
                  ),
                }
              : o,
          ),
        }));
      });

    this.orderItemConfigurationDeleted$.pipe(takeUntilDestroyed()).subscribe((deleteRequest) => {
      const withoutDeletedConfiguration = (i: oofDtos.OrderItem) => ({
        ...i,
        orderItemConfigurations: i.orderItemConfigurations.filter(
          (c) => c.id !== deleteRequest.orderItemConfigurationId,
        ),
        status: deleteRequest.newOrderItemStatus,
      });
      const returnDevicesToPool = (allocations: oofDtos.DeviceAllocation[]) =>
        withDevicesReturnedToPool(allocations, deleteRequest.orderItemConfigurationId);
      this.state.update((state) => ({
        ...state,
        orders: state.orders.map((o) =>
          o.id === deleteRequest.orderId
            ? {
                ...o,
                orderItems: o.orderItems.map((i) =>
                  i.id === deleteRequest.orderItemId
                    ? this.withRedistributedDevices(
                        withoutDeletedConfiguration(i),
                        returnDevicesToPool,
                      )
                    : i,
                ),
              }
            : o,
        ),
      }));
    });

    this.applyValueToAllConfigurationsFromOrder$
      .pipe(
        takeUntilDestroyed(),
        concatMap(({ orderInfo, key, value }) => {
          const versions = this.orders()
            .find((o) => o.id === orderInfo.orderId)
            .orderItems.find((i) => i.id === orderInfo.orderItemId)
            .orderItemConfigurations?.reduce((acc: Record<string, number>, c) => {
              acc[c.id] = c.version;
              return acc;
            }, {});
          const update = {};
          set(update, key, value);
          const body = { versions, update };
          return this.ordersRestService
            .applyToAll(orderInfo.orderId, orderInfo.orderItemId, body)
            .pipe(
              map((result) => ({
                orderId: orderInfo.orderId,
                orderItemId: orderInfo.orderItemId,
                ...result,
              })),
              catchError((err) => {
                console.log(err.message);
                this.error$.next(err);
                return EMPTY;
              }),
            );
        }),
      )
      .subscribe((response) =>
        this.state.update((s) => ({
          ...s,
          orders: s.orders.map((o) =>
            o.id === response.orderId
              ? {
                  ...o,
                  orderItems: o.orderItems.map((i) =>
                    i.id === response.orderItemId
                      ? {
                          ...i,
                          orderItemConfigurations: response.orderItemConfigs,
                          status: response.orderItemStatus,
                        }
                      : i,
                  ),
                }
              : o,
          ),
        })),
      );

    this.deviceAllocationsSaved$
      .pipe(takeUntilDestroyed())
      .subscribe(({ orderId, allocationsByOrderItemId }) =>
        this.state.update((state) => ({
          ...state,
          orders: state.orders.map((o) =>
            o.id === orderId
              ? {
                  ...o,
                  orderItems: o.orderItems.map((i) =>
                    allocationsByOrderItemId[i.id]
                      ? {
                          ...i,
                          deviceAllocations: {
                            ...i.deviceAllocations,
                            allocations: allocationsByOrderItemId[i.id],
                          },
                        }
                      : i,
                  ),
                }
              : o,
          ),
        })),
      );

    this.addressUpdated$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.addressesService.refreshAddresses$.next());
  }

  /**
   * Returns the sum of amounts in all configurations
   * @param orderItem
   */
  getNumberOfAllocatedConfigurations(orderItem: oofDtos.OrderItem): number {
    return orderItem?.orderItemConfigurations
      ? orderItem.orderItemConfigurations.map((config) => config.amount).reduce((a, b) => a + b, 0)
      : 0;
  }
  getRecentConfigVersion(orderId: string, itemId: number, configId: string) {
    return this.orders()
      .find((order) => order.id === orderId)
      .orderItems.find((orderItem) => orderItem.id === itemId)
      .orderItemConfigurations.find((config) => config.id === configId).version;
  }

  private normalizeAdminOrderFilter(
    filter: oofAdminDtos.OrderFilterRequest,
  ): oofAdminDtos.OrderFilterRequest {
    if (filter.keywords?.length) {
      return {
        keywords: filter.keywords,
      };
    }

    return {
      statuses: filter.statuses?.length ? filter.statuses : undefined,
      salesEmails: filter.salesEmails?.length ? filter.salesEmails : undefined,
      customerEmails: this.ordersRestService.toFilterValues(filter.customerEmails),
      fakturaIds: this.ordersRestService.toFilterValues(filter.fakturaIds),
      deviceIds: filter.deviceIds?.length ? filter.deviceIds : undefined,
      keywords: filter.keywords?.length ? filter.keywords : undefined,
    };
  }
}
